import { ThingsToDoPlace, categoryConfig } from '../../../mocks/thingsToDo';

interface PlaceCardProps {
  place: ThingsToDoPlace;
}

export default function PlaceCard({ place }: PlaceCardProps) {
  const cfg = categoryConfig[place.category];

  return (
    <div className="bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-gray-200 transition-all group">
      {/* Image */}
      <div className="relative h-44 overflow-hidden">
        <img
          src={place.image}
          alt={place.name}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>

        {/* Category badge */}
        <div className={`absolute top-3 left-3 inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full ${cfg.accent} ${cfg.color}`}>
          <i className={`${cfg.icon} text-[10px]`}></i>
          {cfg.label}
        </div>

        {/* Cost badge */}
        <div className={`absolute top-3 right-3 text-[10px] font-bold px-2 py-0.5 rounded-full ${
          place.cost === 'Free' ? 'bg-emerald-500 text-white' : 'bg-white/90 text-gray-700'
        }`}>
          {place.cost}
        </div>
      </div>

      {/* Body */}
      <div className="p-4">
        <h3 className="text-sm font-bold text-gray-900 mb-1 leading-snug group-hover:text-rose-500 transition-colors">
          {place.name}
        </h3>

        <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-2">
          <i className="ri-map-pin-line text-xs"></i>
          {place.location}
        </div>

        <p className="text-xs text-gray-500 leading-relaxed line-clamp-3 mb-3">
          {place.description}
        </p>

        {/* Age range */}
        <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-3">
          <i className="ri-user-line text-xs"></i>
          {place.ageRange}
        </div>

        {/* Mom tip */}
        {place.momTip && (
          <div className="bg-pink-50 rounded-lg px-3 py-2 mb-3">
            <p className="text-[11px] text-pink-700 leading-snug">
              <span className="font-bold">Mom Tip:</span> {place.momTip}
            </p>
          </div>
        )}

        {/* Tags */}
        <div className="flex flex-wrap gap-1 mb-3">
          {place.tags.slice(0, 3).map((tag) => (
            <span key={tag} className="text-[10px] font-medium px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full">
              {tag}
            </span>
          ))}
        </div>

        {/* Footer links */}
        <div className="flex items-center justify-between pt-2.5 border-t border-gray-50">
          <a
            href={place.mapLink}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-xs font-semibold text-rose-500 hover:text-rose-700 transition-colors cursor-pointer"
          >
            <i className="ri-map-2-line text-xs"></i>
            Get Directions
          </a>
          {place.website && (
            <a
              href={place.website}
              target="_blank"
              rel="noopener noreferrer"
              className="w-7 h-7 flex items-center justify-center bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-external-link-line text-gray-400 text-xs"></i>
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
